import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { UserDto } from '../../auth/user.dto';
import { environment } from '../../environments/environment';
import { DisplayUserDto } from '../models/display-user.dto';
import { Pagination } from '../pagination/pagination';
import { PaginationOptions } from '../pagination/pagination-options';
import { SignatureDto } from '../signature/signature.dto';
import { ApiService, FileResponse } from './api.service';
import { FileService } from './file.service';
import { UserSiteDto } from '../../auth/userSite.dto';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly userProfileAction: Subject<{ action: 'created' | 'updated' }> = new Subject<{
    action: 'created' | 'updated';
  }>();

  constructor(
    private readonly api: ApiService,
    private readonly fileService: FileService,
  ) {}

  public get userProfileAction$(): Observable<{ action: 'created' | 'updated' }> {
    return this.userProfileAction.asObservable();
  }

  public async findAll(structureId: number): Promise<UserDto[]> {
    const url: string = `${environment.api.userList}/${structureId}/structure`;
    return this.api.get<UserDto[]>(url);
  }

  public async getUsersToCsv(searchValue: string): Promise<void> {
    const uri: string = environment.api.userList + '/admin/exportCSV';
    const { filename, data }: FileResponse = await this.api.postWithFileResponse(uri, { searchValue });
    return this.fileService.saveAs(data, filename);
  }

  public getSignature(): Promise<SignatureDto> {
    const url: string = environment.api.userList + '/signature';
    return this.api.get<SignatureDto>(url);
  }

  public async saveSignature(signature: SignatureDto): Promise<void> {
    const url: string = environment.api.userList + '/signature';
    return this.api.post(url, signature);
  }

  public async searchUsers(searchValue: string, options: PaginationOptions): Promise<Pagination<DisplayUserDto>> {
    return this.api.postPaginated<DisplayUserDto>(options.buildUrl(environment.api.userList), { searchValue });
  }

  public async createUser(user: UserDto): Promise<void> {
    await this.api.post(environment.api.auth + '/register', user);
    this.userProfileAction.next({ action: 'created' });
  }

  public async updateUser(user: UserDto): Promise<void> {
    await this.api.post(environment.api.auth + '/update', user);
    this.userProfileAction.next({ action: 'updated' });
  }

  public async findUser(id: number): Promise<UserDto> {
    const uri: string = environment.api.userList + '/' + id + '/details';
    return await this.api.get<UserDto>(uri);
  }

  public async findSitesForUser(userId: number): Promise<UserSiteDto[]> {
    const url: string = environment.api.userList + '/idUser/' + userId;
    return await this.api.get<UserSiteDto[]>(url);
  }

  public async findAllByStructureId(structureId: number): Promise<UserDto[]> {
    const url: string = environment.api.userList + '/' + structureId + '/structure/';
    return await this.api.get<UserDto[]>(url);
  }
}
